/*
  Nische standalone "Recently viewed" section - for the product page.

  Reads the exact same `nische:recent` localStorage key the collection
  page's rail reads (written by `snippets/nische-recently-viewed.liquid`,
  rendered on every product page). No request is made for any of it: each
  row was built from real Liquid data at the moment that product was viewed,
  and its image is already in the browser's cache.

  Deliberately a SEPARATE file from `nische-collection.js`'s `mountRecent` /
  `bindRail` / `recentCard`, not a shared import - loading that whole file
  (filters, sorting, density toggle, ~900 lines) onto every product page
  just to reuse this one part of it would be pure waste. Scoped to
  `[data-nis-recent-standalone]` specifically (not the collection page's
  bare `[data-nis-recent]`) so the two never address the same element even
  if both scripts ever ended up on one page.
*/
(function () {
  'use strict';

  if (window.NischeRecentRailStandalone) return;
  window.NischeRecentRailStandalone = true;

  var RECENT_KEY = 'nische:recent';

  function q(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* localStorage throws outright in some privacy modes rather than
     returning null, so every touch of it is wrapped. Losing the rail is the
     whole cost - there is nothing to report to the shopper. */
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* full, or blocked */
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  /* Renders the SAME `.nc-*` card markup the product grid does, so this is
     literally the product card rather than a lookalike - including the
     compare-at price and the discount pill. */
  function recentCard(row, mode) {
    var ratio = 1;
    var needsFix = false;
    if (mode === 'portrait') {
      ratio = 0.8;
    } else if (mode === 'adapt') {
      if (row.ratio) ratio = row.ratio;
      else needsFix = true;
    }

    var media = '';
    if (row.img) {
      media =
        '<img src="' +
        escapeAttr(row.img) +
        '"' +
        (row.srcset ? ' srcset="' + escapeAttr(row.srcset) + '"' : '') +
        // Its own `sizes`, not the grid's: these cards are ~150px wide, and
        // inheriting a larger hint would pull a desktop-sized file for them.
        ' sizes="150px" alt="' +
        escapeAttr(row.alt || '') +
        '" loading="lazy"' +
        (needsFix ? ' data-nis-fixratio' : '') +
        '>';
    }

    var prices = '<span class="nc-price">' + escapeHtml(row.price || '') + '</span>';
    if (row.was) prices += '<s class="nc-price--was">' + escapeHtml(row.was) + '</s>';
    if (row.off) prices += '<span class="nc-off">' + escapeHtml(row.off) + '</span>';

    return (
      '<a class="nc-wrap nis-recent__card" href="' +
      escapeAttr(row.url) +
      '">' +
      '<div class="nc-card" style="--nc-ratio: ' +
      ratio +
      ';">' +
      '<div class="nc-card__well"><div class="nc-card__media">' +
      media +
      '</div></div>' +
      '<div class="nc-card__body">' +
      '<div class="nc-card__titlerow"><h3 class="nc-card__title">' +
      escapeHtml(row.title) +
      '</h3></div>' +
      '<div class="nc-card__pricerow">' +
      prices +
      '</div></div></div></a>'
    );
  }

  /* The desktop arrows. A touch device gets its affordance from the card
     peeking in at the edge of the screen; a mouse gets nothing, so these
     say "there is more this way" - and disappear entirely when there is
     not. */
  function bindRail(section, rail) {
    var navs = qa('[data-nis-rail]', section);
    if (!navs.length) return;

    var sync = function () {
      var overflows = rail.scrollWidth > rail.clientWidth + 2;
      navs.forEach(function (btn) {
        btn.hidden = !overflows;
      });
      if (!overflows) return;
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      navs.forEach(function (btn) {
        btn.disabled = btn.getAttribute('data-nis-rail') === 'prev' ? atStart : atEnd;
      });
    };

    navs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = Math.max(rail.clientWidth * 0.8, 200);
        rail.scrollBy({
          left: btn.getAttribute('data-nis-rail') === 'next' ? step : -step,
          behavior: 'smooth'
        });
      });
    });

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', debounce(sync, 200));

    sync();
    window.setTimeout(sync, 400);
  }

  function mount(section) {
    var rail = q('[data-nis-recent-rail]', section);

    var count = parseInt(section.getAttribute('data-nis-recent-count'), 10);
    if (!count || count < 1) count = 8;
    var mode = section.getAttribute('data-nis-recent-ratio') || 'square';

    var list = [];
    try {
      list = JSON.parse(readStore(RECENT_KEY) || '[]');
    } catch (e) {
      list = [];
    }

    var here = window.location.pathname;
    list = list
      .filter(function (row) {
        return row && row.url && row.url !== here;
      })
      .slice(0, count);

    if (!list.length || !rail) {
      section.hidden = true;
      return;
    }

    rail.innerHTML = list.map(function (row) { return recentCard(row, mode); }).join('');
    section.hidden = false;

    // Older records predate the stored ratio. Rather than leave those cards
    // a different shape, correct them once the image reports its own size.
    if (mode === 'adapt') {
      qa('img[data-nis-fixratio]', rail).forEach(function (img) {
        var apply = function () {
          if (!img.naturalWidth || !img.naturalHeight) return;
          var card = img.closest('.nc-card');
          if (card) card.style.setProperty('--nc-ratio', img.naturalWidth / img.naturalHeight);
        };
        if (img.complete) apply();
        else img.addEventListener('load', apply, { once: true });
      });
    }

    var clear = q('[data-nis-recent-clear]', section);
    if (clear) {
      clear.addEventListener('click', function () {
        writeStore(RECENT_KEY, '[]');
        section.hidden = true;
      });
    }

    bindRail(section, rail);
  }

  function boot() {
    qa('[data-nis-recent-standalone]').forEach(function (section) {
      if (section.nisRailBound) return;
      section.nisRailBound = true;
      mount(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /*
    The theme editor swaps a section's markup back in fresh (via the Section
    Rendering API) on every settings change while previewing, WITHOUT a full
    page load - the section starts `hidden` again in that fresh HTML, and
    with no DOMContentLoaded to re-run boot(), it stayed hidden until a real
    save or reload. `shopify:section:load` fires exactly when that swap
    happens, target being the section's `#shopify-section-*` wrapper.
  */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    var section = target && target.matches && target.matches('[data-nis-recent-standalone]')
      ? target
      : q('[data-nis-recent-standalone]', target);
    if (!section) return;
    section.nisRailBound = true;
    mount(section);
  });
})();
